import React, { useEffect, useState } from 'react';
import {
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  IconButton,
} from '@mui/material';
import ServiceConfigFormattedMessages from '../../../../messages/admin/ServiceConfigFormattedMessages';
import useRouteRequest from '../../../../requests/RouteRequest';
import ConfirmModal from '../../../common/modals/ConfirmModal';
import EditCarrier from './RouteEditCarrier';
import { Delete, Edit } from '@mui/icons-material';
import CustomModal from '../../../common/CustomModal';

const RouteView = ({ show, route, onEdit, onClose, errorHandler }) => {
  const [loading, setLoading] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const [carriers, setCarriers] = useState([]);
  const [filteredCarriers, setFilteredCarriers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [allCarriers, setAllCarriers] = useState([]);
  const [carrierToDelete, setCarrierToDelete] = useState({});

  const [editCarrier, setEditCarrier] = useState({});
  const { getCarriersByRoute, getCarriers, deleteCarrier } = useRouteRequest();

  useEffect(() => {
    if (route.route_id) {
      fetchCarriers();
    }
  }, [route]);

  useEffect(() => {
    filterCarriers();
  }, [carriers, editCarrier]);

  const fetchCarriers = async () => {
    setLoading(true);
    await Promise.all([
      getCarriersByRoute(route.route_id, errorHandler),
      getCarriers(errorHandler),
    ]).then(([carriers, allCarriers]) => {
      setCarriers(carriers);
      setAllCarriers(allCarriers);
      setLoading(false);
    });
  };

  const openAddCarrier = () => {
    setAddOpen(true);
    setFilteredCarriers(carriers);
  };

  const filterCarriers = () => {
    const filteredCarriers = carriers.filter((carrier) => {
      if (
        editCarrier.carrier_id &&
        carrier.carrier_id !== editCarrier.carrier_id
      ) {
        return false;
      }

      if (
        editCarrier.provider_id &&
        carrier.provider_id !== editCarrier.provider_id
      ) {
        return false;
      }

      return true;
    });

    setFilteredCarriers(filteredCarriers);
  };

  const handleSaveCarrier = async () => {
    setAddOpen(false);
    setEditCarrier({});
    fetchCarriers();
  };

  const handleCancelAddCarrier = () => {
    setAddOpen(false);
    setEditCarrier({});
  };

  const handleEditRoute = () => {
    onClose();
    onEdit(route);
  };

  const handleEditCarrier = (carrier) => {
    setEditCarrier(carrier);
    setAddOpen(true);
  };

  const confirmDelete = (carrier) => {
    setShowConfirmDelete(true);
    setCarrierToDelete(carrier);
  };

  const handleDeleteCarrier = async () => {
    await deleteCarrier(carrierToDelete.carrier_route_id, errorHandler);
    setShowConfirmDelete(false);
    fetchCarriers();
  };

  return (
    <>
      <CustomModal title="Route Details" open={show} handleClose={onClose}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Typography>
              <strong>{ServiceConfigFormattedMessages.routeId}:</strong>{' '}
              {route.route_id}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography>
              <strong>{ServiceConfigFormattedMessages.routeName}:</strong>{' '}
              {route.route_name}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography>
              <strong>{ServiceConfigFormattedMessages.class}:</strong>{' '}
              {route.route_class_meaning}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography>
              <strong>{ServiceConfigFormattedMessages.type}:</strong>{' '}
              {route.route_type_meaning}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography>
              <strong>{ServiceConfigFormattedMessages.status}:</strong>{' '}
              {route.route_status_meaning}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Typography>
              <strong>{ServiceConfigFormattedMessages.priority}:</strong>{' '}
              {route.priority}
            </Typography>
          </Grid>
          <Grid size={12}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleEditRoute}
            >
              Edit Route
            </Button>
          </Grid>
        </Grid>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 2 }}
        >
          <Grid size={12}>
            <Typography variant="h2">Carriers</Typography>
          </Grid>
          <Grid size={12}>
            {!addOpen && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={openAddCarrier}
              >
                Add Carrier
              </Button>
            )}
          </Grid>
        </Grid>

        {addOpen && (
          <EditCarrier
            route={route}
            carrier={editCarrier}
            allCarriers={allCarriers}
            onCancel={handleCancelAddCarrier}
            onSave={handleSaveCarrier}
            errorHandler={errorHandler}
          />
        )}
        <Grid container sx={{ mt: 2 }}>
          <Grid size={12}>
            <TableContainer sx={{ marginBottom: 4, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {ServiceConfigFormattedMessages.provider}
                    </TableCell>
                    <TableCell>
                      {ServiceConfigFormattedMessages.carrier}
                    </TableCell>
                    <TableCell align="right">
                      {ServiceConfigFormattedMessages.priority}
                    </TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Grid
                          container
                          justifyContent="center"
                          alignItems="center"
                          spacing={2}
                        >
                          <Grid size={12}>
                            <CircularProgress size={24} />
                          </Grid>
                          <Grid size={12}>
                            <Typography>
                              {ServiceConfigFormattedMessages.loading}
                            </Typography>
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  ) : carriers.length > 0 ? (
                    filteredCarriers.map((carrier) => (
                      <TableRow key={carrier.carrier_route_id}>
                        <TableCell title={carrier.provider_name}>
                          {carrier.provider_id}
                        </TableCell>
                        <TableCell>{carrier.carrier_name}</TableCell>
                        <TableCell align="right">{carrier.priority}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleEditCarrier(carrier)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => confirmDelete(carrier)}
                          >
                            <Delete color="error" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography>No carriers found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </CustomModal>

      <ConfirmModal
        show={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        onConfirm={() => handleDeleteCarrier()}
        message={`Are you sure you want to delete ${carrierToDelete.carrier_name} for provider ${carrierToDelete.provider_name}?`}
      />
    </>
  );
};

export default RouteView;
